/**
 * Plan of Record — Official Record.
 *
 * Renders a FROZEN por_record_snapshot (migration 648), not the live rows:
 * what was ratified, exactly as it was ratified, stamped with version,
 * authority, ratifier, and timestamp. The latest non-superseded snapshot is
 * the record of authority; older versions remain readable with a superseded
 * notice. Reuses PorSynopsis as the document body — the snapshot payload IS
 * an assemble_por composition, so the official document and the live synopsis
 * can never drift in rendering.
 *
 * Like the synopsis: always available, shareable, printable.
 * URLs: /planning/plan-of-record/:id/official (latest)
 *       /planning/plan-of-record/:id/official/:version
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, ShieldCheck, History } from 'lucide-react';
import PorSynopsis from './PorSynopsis';
import {
  getPorSnapshot,
  listPorSnapshots,
  type PorRecordSnapshot,
} from '../../lib/planOfRecordService';
import type { AssembledPor } from '../../types/plan-of-record';
import { formatDate } from '../../components/plan-of-record/porUi';
import { PorAddressPanel } from '../../components/plan-of-record/PorAddressPanel';

export default function PorOfficialRecord({ porId }: { porId: string }) {
  const { version } = useParams<{ version?: string }>();
  const requestedVersion = version != null ? Number(version) : undefined;

  const [snapshot, setSnapshot] = useState<PorRecordSnapshot | null>(null);
  const [versions, setVersions] = useState<Omit<PorRecordSnapshot, 'payload'>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all([
      getPorSnapshot(porId, Number.isFinite(requestedVersion) ? requestedVersion : undefined),
      listPorSnapshots(porId),
    ]).then(([snap, list]) => {
      if (cancelled) return;
      if (snap.error) setError(snap.error);
      setSnapshot(snap.data);
      setVersions(list.data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [porId, requestedVersion]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-[var(--text-ter)]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center space-y-3">
        <ShieldCheck className="h-8 w-8 mx-auto text-[var(--text-ter)]" />
        <h1 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          No ratified record yet
        </h1>
        <p className="text-sm text-[var(--text-ter)]">
          {error ??
            'This Plan of Record has not been ratified. Ratify it from the dashboard to create ratified record v1.'}
        </p>
        <Link
          to={`/planning/plan-of-record/${porId}/dashboard`}
          className="inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          Back to dashboard
        </Link>
      </div>
    );
  }

  const isAuthoritative = snapshot.superseded_by_snapshot_id === null;
  const provenance: string[] = [];
  if (snapshot.source_cycle_report_id) provenance.push('ratified priority list (cycle report)');
  if (snapshot.source_plan_version_id) provenance.push('committed plan version (engine)');

  return (
    <div className="space-y-4">
      {/* ── The address + share links (migration 721) ───────────────────── */}
      <PorAddressPanel porId={porId} snapshot={snapshot} versions={versions} />

      {/* ── Official stamp ─────────────────────────────────────────────── */}
      <div
        className={` border px-5 py-4 print:break-inside-avoid ${
          isAuthoritative
            ? 'border-[var(--green)] bg-[var(--green-dim)] dark:border-[var(--green)] dark:bg-[var(--green)]/30'
            : 'border-[var(--mustard)] bg-[var(--mustard-dim)] dark:border-[var(--mustard)] dark:bg-[var(--mustard)]/30'
        }`}
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            <ShieldCheck
              className={`h-4 w-4 ${isAuthoritative ? 'text-[var(--green)]' : 'text-[var(--mustard)]'}`}
            />
            Ratified Record · v{snapshot.version}
          </span>
          <span className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Ratified {formatDate(snapshot.authorized_at)} under authority of{' '}
            <strong>{snapshot.ratification_authority}</strong>
          </span>
          {!isAuthoritative && (
            <span className="text-xs font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">
              Superseded — this is a historical version, not the record of authority.
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Rationale: {snapshot.rationale}
          {provenance.length > 0 && <> · Frozen provenance: {provenance.join(' + ')}</>}
        </p>
      </div>

      {/* ── Version trail ──────────────────────────────────────────────── */}
      {versions.length > 1 && (
        <div className="flex items-center gap-2 text-xs text-[var(--text-ter)] print:hidden">
          <History className="h-3.5 w-3.5" />
          {versions.map((v) => (
            <Link
              key={v.id}
              to={`/planning/plan-of-record/${porId}/official/${v.version}`}
              className={
                v.version === snapshot.version
                  ? 'font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)]'
                  : 'hover:text-primary-600 hover:underline'
              }
            >
              v{v.version}
            </Link>
          ))}
        </div>
      )}

      {/* ── The frozen document ────────────────────────────────────────── */}
      <PorSynopsis assembled={snapshot.payload as unknown as AssembledPor} />
    </div>
  );
}
